import { PlaybookDoc, PlaybookSection } from './types';

export const MIN_SEARCH_LENGTH = 2;
const SNIPPET_RADIUS = 60;

export interface PlaybookSearchHit {
  section: PlaybookSection;
  /** `Parent › Section` */
  label: string;
  before: string;
  hit: string;
  after: string;
}

/** Plain text of a markdown fragment: table pipes, emphasis marks and code ticks removed. */
const toPlainText = (markdown: string): string =>
  markdown
    .replace(/^\s*\|?[\s:|-]+\|?\s*$/gm, ' ')
    .replace(/[|*`>#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getLabel = (doc: PlaybookDoc, section: PlaybookSection): string => {
  const parent = section.parentId ? doc.byId[section.parentId] : undefined;
  return parent ? `${parent.title} › ${section.title}` : section.title;
};

/** Full-text search over one language file. Matches the title or the body of every section. */
export const searchPlaybook = (doc: PlaybookDoc, keyword: string): PlaybookSearchHit[] => {
  const kw = keyword.trim().toLowerCase();
  if (kw.length < MIN_SEARCH_LENGTH) return [];

  const hits: PlaybookSearchHit[] = [];
  doc.sections.forEach((section) => {
    const text = toPlainText(section.body);
    const textIndex = text.toLowerCase().indexOf(kw);
    const inTitle = section.title.toLowerCase().includes(kw);
    if (textIndex < 0 && !inTitle) return;

    if (textIndex < 0) {
      hits.push({
        section,
        label: getLabel(doc, section),
        before: '',
        hit: '',
        after: text.slice(0, SNIPPET_RADIUS * 2),
      });
      return;
    }
    const start = Math.max(0, textIndex - SNIPPET_RADIUS);
    const end = Math.min(text.length, textIndex + kw.length + SNIPPET_RADIUS);
    hits.push({
      section,
      label: getLabel(doc, section),
      before: `${start > 0 ? '…' : ''}${text.slice(start, textIndex)}`,
      hit: text.slice(textIndex, textIndex + kw.length),
      after: `${text.slice(textIndex + kw.length, end)}${end < text.length ? '…' : ''}`,
    });
  });
  return hits;
};
